import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Button,
} from "@mui/material";

import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddIcon from "@mui/icons-material/Add";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import api from "../../services/api/axios";
import { useAuth } from "../../providers/AuthProvider";

import DashboardSignalRow from "./DashboardSignalRow";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const CARD_SX = {
  height: "100%",
  border: "1px solid rgba(0,51,108,0.12)",
  borderRadius: "12px",
  boxShadow: "0 1px 3px rgba(15,23,42,0.05), 0 8px 20px rgba(15,23,42,0.06)",
  background: "#ffffff",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: BRAND_BLUE,
  },
};

const ACTION_BTN_SX = {
  minHeight: 34,
  px: 1.25,
  borderRadius: "8px",
  border: "1.5px solid rgba(0,51,108,0.18)",
  color: BRAND_BLUE,
  backgroundColor: "#eef2f8",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 12.5,
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: "#e2eaf5",
    borderColor: "rgba(0,51,108,0.4)",
  },
};

const PRIMARY_ACTION_BTN_SX = {
  ...ACTION_BTN_SX,
  border: `1.5px solid ${BRAND_GREEN}`,
  color: "#ffffff",
  backgroundColor: BRAND_GREEN,
  boxShadow: "0 2px 8px rgba(100,131,21,0.2)",
  "&:hover": {
    backgroundColor: "#536e12",
    borderColor: "#536e12",
    boxShadow: "0 4px 12px rgba(100,131,21,0.24)",
  },
};

function ProspectingHomeSummary() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [websiteSummary, setWebsiteSummary] = useState(null);
  const [prospectingSummary, setProspectingSummary] = useState(null);
  const [inquirySummary, setInquirySummary] = useState(null);

  /* --------------------------------------------------
     Website visits (Last 24 Hours)
  -------------------------------------------------- */
  useEffect(() => {
    if (!user?.organization_guid) return;

    const until = dayjs().startOf("day").toISOString();
    const since = dayjs().startOf("day").subtract(1, "day").toISOString();

    api
      .get(`/website-visits/${user.organization_guid}/summary`, {
        params: { since, until },
      })
      .then((res) => setWebsiteSummary(res.data))
      .catch(() => setWebsiteSummary(null));
  }, [user]);

  /* --------------------------------------------------
     Prospecting summary
  -------------------------------------------------- */
  useEffect(() => {
    if (!user) return;

    const since = dayjs().subtract(30, "day").toISOString();

    api
      .get("/prospecting-companies/summary", { params: { since } })
      .then((res) => setProspectingSummary(res.data))
      .catch(() => setProspectingSummary(null));
  }, [user]);

  /* --------------------------------------------------
     Inquiry summary
  -------------------------------------------------- */
  useEffect(() => {
    if (!user) return;

    api
      .get("/inquiries/summary")
      .then((res) => setInquirySummary(res.data))
      .catch(() => setInquirySummary(null));
  }, [user]);

  const websiteUntilParam = dayjs().startOf("day").toISOString();
  const websiteSinceParam = dayjs()
    .startOf("day")
    .subtract(1, "day")
    .toISOString();

  return (
    <Card sx={CARD_SX}>
      <CardContent
        sx={{
          p: 2.75,
          pt: 3,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Card Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.25 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: BRAND_GREEN,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
              boxShadow: `0 2px 8px ${BRAND_GREEN}40`,
            }}
          >
            <TravelExploreIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: BRAND_BLUE,
                fontSize: 17,
                fontWeight: 800,
                lineHeight: 1.15,
              }}
            >
              Prospecting &amp; Business Attraction
            </Typography>
            <Typography
              sx={{
                color: "#5c718f",
                fontSize: 12.5,
                mt: 0.35,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
              }}
            >
              Prospecting Pipeline
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "20px",
              backgroundColor: "rgba(100,131,21,0.08)",
              border: "1px solid rgba(100,131,21,0.18)",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                color: BRAND_GREEN,
                letterSpacing: 0.7,
                textTransform: "uppercase",
              }}
            >
              Prospecting
            </Typography>
          </Box>
        </Box>

        {/* Signal Rows */}
        <Stack spacing={0.85} sx={{ flex: 1 }}>
          <DashboardSignalRow
            icon={<TravelExploreIcon />}
            label="Website Visits (Last 24 Hours)"
            value={websiteSummary?.unique_companies ?? 0}
            color="#00838F"
            zeroText="No recent visits"
            tooltip="Unique companies identified from website activity during the last 24 hours."
            onClick={() =>
              navigate(
                `/website-visits?since=${encodeURIComponent(websiteSinceParam)}&until=${encodeURIComponent(websiteUntilParam)}`,
              )
            }
          />
          <DashboardSignalRow
            icon={<HelpOutlineIcon />}
            label="New Inquiries (Last 7 Days)"
            value={inquirySummary?.new_inquiries ?? 0}
            color="#B8860B"
            zeroText="No new inquiries"
            tooltip="New inbound inquiries received within the past seven days."
            onClick={() => navigate("/inquiries?recent_days=7")}
          />
          <DashboardSignalRow
            icon={<PersonSearchIcon />}
            label="Prospects Awaiting Outreach"
            value={prospectingSummary?.awaiting_outreach ?? 0}
            color="#ED6C02"
            priority
            zeroText="All caught up"
            tooltip="Prospect companies that have been identified but still need an initial outreach or follow-up."
            onClick={() =>
              navigate(
                "/prospecting-companies?pipeline_stage=New%20-%20Needs%20Outreach",
              )
            }
          />
          <DashboardSignalRow
            icon={<TrendingUpIcon />}
            label="New Leads Identified - In Conversation"
            value={prospectingSummary?.new_leads_identified ?? 0}
            color={BRAND_GREEN}
            tooltip="Prospect companies recently added or moved into the in conversation stage."
            onClick={() =>
              navigate(
                "/prospecting-companies?pipeline_stage=In%20Conversation",
              )
            }
          />
          <DashboardSignalRow
            icon={<WorkspacePremiumIcon />}
            label="Qualified Opportunities"
            value={prospectingSummary?.qualified_opportunities ?? 0}
            color="#6D28D9"
            priority
            zeroText="No qualified opportunities yet"
            tooltip="Prospect companies currently marked as qualified opportunities."
            onClick={() =>
              navigate(
                "/prospecting-companies?pipeline_stage=Qualified%20Opportunity",
              )
            }
          />
        </Stack>

        <Divider sx={{ my: 2, borderColor: "rgba(0,51,108,0.07)" }} />

        {/* Quick Actions */}
        <Box>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: "#7b90b2",
              letterSpacing: 1.1,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Quick Actions
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => navigate("/prospecting-companies/new")}
              sx={{ ...PRIMARY_ACTION_BTN_SX, flex: 1.1 }}
            >
              Add Prospect
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => navigate("/prospecting-contacts/new")}
              sx={{ ...ACTION_BTN_SX, flex: 1 }}
            >
              Add Contact
            </Button>
            <Button
              variant="outlined"
              startIcon={
                <ArrowForwardIcon sx={{ fontSize: "14px !important" }} />
              }
              onClick={() => navigate("/prospecting-companies")}
              sx={{ ...ACTION_BTN_SX, flex: 1 }}
            >
              View All
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ProspectingHomeSummary;
